/**
 * Picked Files → browser `File` objects.
 *
 * The fallback for uploaders whose consumer only understands `File[]`: the
 * bytes of each picked document are streamed from Files (`/files/stream-file`
 * through `apis`, `responseType: 'blob'`) and wrapped with the right name and
 * type, so "select from Files" works in every uploader without a backend
 * change. Consumers that can take Files uuids directly should prefer
 * `onPickExisting` - nothing then travels through the browser.
 */

import { streamFile } from '../../../utils/FileApiBuilder';
import type { PickedFile } from './filesPickerTypes';

export interface PickedFileDownloadResult {
  files: File[];
  failed: PickedFile[];
}

export async function downloadPickedFiles(picked: PickedFile[]): Promise<PickedFileDownloadResult> {
  const results = await Promise.all(
    picked.map(async (p) => {
      try {
        const blob = await streamFile(p.uuid, p.folder_uuid || '', undefined, 'attachment');
        if (!blob || !(blob instanceof Blob) || blob.size === 0) return { p, file: null };
        const type = p.mime_type || blob.type || 'application/octet-stream';
        return { p, file: new File([blob], p.name, { type, lastModified: Date.now() }) };
      } catch {
        return { p, file: null };
      }
    }),
  );
  return {
    files: results.filter((r) => r.file).map((r) => r.file as File),
    failed: results.filter((r) => !r.file).map((r) => r.p),
  };
}
